/**
 * SiteTreePanel — Lot 3, 3.1 : l'organigramme des sites, à l'écran.
 *
 * Onglet « Organisation » de `SitesView`. Arbre indenté (`<ul>` récursif, pas de bibliothèque
 * d'arbre) : chaque nœud porte son compte de véhicules PAR ATTACHE et ses responsables DIRECTS
 * (`GET /sites/tree`). « Rattacher à… » pose `PUT /sites/:id/parent` — le serveur refuse le
 * cycle et la profondeur excessive (`CYCLE_DETECTED`, `MAX_DEPTH_EXCEEDED`), ce panneau ne fait
 * que relayer le refus.
 *
 * Le périmètre affiché est déjà celui de l'appelant (`GET /sites/tree` le borne côté serveur) :
 * un gestionnaire restreint ne voit QUE ses sites, jamais toute l'org.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { GitBranch, Car, Users, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { siteOrgAPI } from '../../api/siteOrg.js';

/** Aplati l'arbre en une liste plate `{id, site_name, depth}` — pour le sélecteur de parent. */
function flattenTree(nodes, depth = 0, out = []) {
  for (const n of nodes || []) {
    out.push({ id: n.id, site_name: n.site_name, depth });
    flattenTree(n.children, depth + 1, out);
  }
  return out;
}

/** Ids de `node` et de tous ses descendants — pour ne jamais proposer un descendant comme parent. */
function subtreeIds(node, out = new Set()) {
  out.add(node.id);
  for (const c of node.children || []) subtreeIds(c, out);
  return out;
}

function ERROR_LABEL_KEY(code) {
  switch (code) {
    case 'CYCLE_DETECTED': return 'sites.org.errors.cycle';
    case 'MAX_DEPTH_EXCEEDED': return 'sites.org.errors.maxDepth';
    case 'SITE_ORG_MISMATCH': return 'sites.org.errors.orgMismatch';
    default: return 'sites.org.errors.generic';
  }
}

function TreeNode({ node, allOptions, onReparent, depth = 0 }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [picking, setPicking] = useState(false);
  const hasChildren = (node.children || []).length > 0;
  const excluded = useMemo(() => subtreeIds(node), [node]);

  return (
    <li>
      <div
        className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-50"
        style={{ paddingLeft: `${depth * 20}px` }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`p-0.5 text-gray-400 hover:text-gray-600 ${hasChildren ? '' : 'invisible'}`}
          aria-label={expanded ? t('sites.org.collapse') : t('sites.org.expand')}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <span className="font-medium text-gray-900">{node.site_name}</span>
        <span className="text-xs text-gray-400">{node.site_code}</span>

        <span className="flex items-center gap-1 text-xs text-gray-500 ml-2">
          <Car className="w-3.5 h-3.5" /> {node.vehicle_count || 0}
        </span>

        {(node.managers || []).length > 0 && (
          <span className="flex items-center gap-1 text-xs text-purple-600" title={(node.managers || []).map(m => m.name || `#${m.user_id}`).join(', ')}>
            <Users className="w-3.5 h-3.5" /> {node.managers.length}
          </span>
        )}

        <div className="ml-auto">
          {picking ? (
            <select
              autoFocus
              defaultValue=""
              onBlur={() => setPicking(false)}
              onChange={(e) => {
                const value = e.target.value === '' ? null : Number(e.target.value);
                setPicking(false);
                onReparent(node.id, value);
              }}
              className="text-xs border border-gray-300 rounded px-2 py-1"
            >
              <option value="">{t('sites.org.rootOption')}</option>
              {allOptions
                .filter((o) => !excluded.has(o.id))
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {'  '.repeat(o.depth)}{o.site_name}
                  </option>
                ))}
            </select>
          ) : (
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="text-xs text-purple-600 hover:text-purple-800 font-medium"
            >
              {t('sites.org.reattach')}
            </button>
          )}
        </div>
      </div>

      {hasChildren && expanded && (
        <ul>
          {node.children.map((child) => (
            <TreeNode key={child.id} node={child} allOptions={allOptions} onReparent={onReparent} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

function SiteTreePanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['siteOrgTree'],
    queryFn: () => siteOrgAPI.getTree(),
  });

  const tree = data?.tree || [];
  const allOptions = useMemo(() => flattenTree(tree), [tree]);

  const reparentMutation = useMutation({
    mutationFn: ({ siteId, parentSiteId }) => siteOrgAPI.setParent(siteId, parentSiteId),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['siteOrgTree'] });
    },
    onError: (err) => {
      // `err.code` porte le code métier (`apiRequest`, core.js) — jamais `err.data.code`.
      setError(err?.code || 'generic');
    },
  });

  const handleReparent = (siteId, parentSiteId) => {
    reparentMutation.mutate({ siteId, parentSiteId });
  };

  if (isLoading) {
    return <p className="text-gray-500 text-center py-8">{t('common.states.loading')}</p>;
  }
  if (isError) {
    return <p className="text-red-500 text-center py-8">{t('sites.org.loadError')}</p>;
  }

  return (
    <div className="space-y-4" data-testid="site-tree-panel">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <GitBranch className="w-4 h-4 text-purple-600" />
        <span>{t('sites.org.title')}</span>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {t(ERROR_LABEL_KEY(error))}
        </div>
      )}

      {tree.length === 0 ? (
        <p className="text-gray-500 text-center py-8">{t('sites.org.empty')}</p>
      ) : (
        <ul className="border border-gray-100 rounded-lg divide-y divide-gray-50">
          {tree.map((root) => (
            <TreeNode key={root.id} node={root} allOptions={allOptions} onReparent={handleReparent} />
          ))}
        </ul>
      )}

      <p className="text-xs text-gray-400">{t('sites.org.effectiveNextLogin')}</p>
    </div>
  );
}

export default SiteTreePanel;
